import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n'; // Подключаем i18n
import CustomerDetailsForm from './CustomerDetailsForm';

const bankLogos = [
    { name: 'Swedbank Estonia', logo: '/bank-logos/Swedbank_Estonia.png', code: 'HABAEE2X' },
    { name: 'SEB Estonia', logo: '/bank-logos/SEB_Estonia.png', code: 'EEUHEE2X' },
    { name: 'LHV Estonia', logo: '/bank-logos/LHV_Estonia.png', code: 'LHVBEE22' },
    { name: 'Luminor Estonia', logo: '/bank-logos/Luminor_Estonia.png', code: 'RIKOEE22' },
    { name: 'Coop Pank', logo: '/bank-logos/Coop_Pank.png', code: 'EKRDEE22' },
    { name: 'Citadele Estonia', logo: '/bank-logos/Citadele_Estonia.png', code: 'PARXEE22' },
    { name: 'Revolut Estonia', logo: '/bank-logos/Revolut_Estonia.png', code: 'RVUALT2V' },
    { name: 'N26 Estonia', logo: '/bank-logos/N26_Estonia.svg', code: 'NTSBDEB1' },
];

const cardPayment = { logo: '/bank-logos/visa-mc-ap-gp.png' };

const PRE_CHECK_ENDPOINT = 'https://api.wiseclean.ee/api:56Z3lZ-0/old_order/pre_check_client_info';
const SUPPORT_EMAIL = 'info@wiseclean.eu';

// The order is created asynchronously by the app, then the client is redirected here
// immediately. The order row may not exist in Airtable yet, so pre-check returns 5xx
// ("record not found"). Retry a few times with a short delay before giving up, so the
// spinner stays up while the order finishes saving instead of flashing an error screen.
const PRE_CHECK_MAX_RETRIES = 2; // retries after the first attempt (3 total)
const PRE_CHECK_RETRY_DELAY_MS = 2000;

function Payment({ orderIdOverride = '' }) {
    const { lang, orderId: routeOrderId } = useParams();
    // PaymentRoute passes the legacy-form id (<recid>, <recid>completed or
    // <recid>tips) when the EveryPay page hands a link back: a ?subject= link, a
    // bare rec id or a suffix link. ws1 reads the table and the amount from it.
    const orderId = orderIdOverride || routeOrderId;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [step, setStep] = useState('checking'); // 'checking' | 'collect' | 'select' | 'error'

    useEffect(() => {
        if (['en', 'et'].includes(lang)) {
            i18n.changeLanguage(lang);
        } else {
            navigate(`/external/payment/en/${orderId}`);
        }
    }, [lang, orderId, i18n, navigate]);

    useEffect(() => {
        if (!orderId) return undefined;
        let cancelled = false;
        let retryTimer = null;
        const url = `${PRE_CHECK_ENDPOINT}?orderID=${encodeURIComponent(orderId)}`;

        const scheduleRetry = (attempt) => {
            retryTimer = setTimeout(() => runPreCheck(attempt + 1), PRE_CHECK_RETRY_DELAY_MS);
        };

        const runPreCheck = (attempt) => {
            fetch(url)
                .then(async (r) => {
                    if (r.ok) {
                        return { ok: true, data: await r.json() };
                    }
                    return { ok: false, status: r.status };
                })
                .then((res) => {
                    if (cancelled) return;

                    if (res.ok) {
                        const data = res.data;
                        // hasUser:false — намеренная permanent-ошибка (orphan-заказ без UserID).
                        // Повтор не поможет: показываем error screen сразу.
                        if (!data || data.hasUser === false) {
                            setStep('error');
                            return;
                        }
                        setStep(data.needsInput ? 'collect' : 'select');
                        return;
                    }

                    // HTTP 5xx обычно значит «заказ ещё не появился в Airtable» (асинхронное
                    // создание не завершилось) — это transient, повторяем со спиннером.
                    // HTTP 4xx — клиентская ошибка (битый orderID), повтор бесполезен.
                    if (res.status >= 500 && attempt < PRE_CHECK_MAX_RETRIES) {
                        scheduleRetry(attempt);
                        return;
                    }
                    setStep('error');
                })
                .catch(() => {
                    if (cancelled) return;
                    // Network / JSON-parse fail — тоже transient: повторяем, пока есть попытки.
                    if (attempt < PRE_CHECK_MAX_RETRIES) {
                        scheduleRetry(attempt);
                        return;
                    }
                    // Fail-closed: после всех попыток не пускаем в bank selector, показываем
                    // error screen. Лучше «обратитесь в поддержку», чем платёж с битыми contact info.
                    setStep('error');
                });
        };

        setStep('checking');
        runPreCheck(0);

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [orderId]);

    // Отправка данных о платеже
    const handlePayment = (paymentMethod, preferredProvider = null) => {
        // Формируем URL с параметрами
        const params = new URLSearchParams({
            orderID: orderId,
            paymentMethod,
            locale: lang,
            ...(preferredProvider && { preferredProvider }), // Добавляем только если есть код банка
        });

        const redirectUrl = `https://api.wiseclean.ee/api:56Z3lZ-0/old_order/create_payment_link_new?${params.toString()}`;
        console.log('Redirecting to:', redirectUrl);

        // Перенаправляем пользователя на API
        window.location.href = redirectUrl;
    };

    if (step === 'checking') {
        return (
            <div style={checkingStyles.page}>
                <style>{`
                    @keyframes wc-payment-spin { to { transform: rotate(360deg); } }
                    @media (prefers-reduced-motion: reduce) {
                        .wc-payment-spinner { animation-duration: 2400ms; }
                    }
                `}</style>
                <header style={checkingStyles.header}>
                    <img
                        src="/wiseclean-logo.png"
                        alt="WiseClean"
                        style={checkingStyles.logo}
                    />
                </header>
                <main style={checkingStyles.main}>
                    <div className="wc-payment-spinner" style={checkingStyles.spinner} aria-hidden="true" />
                    <div style={checkingStyles.text} role="status" aria-live="polite">
                        {t('paymentPreparing')}
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div style={errorStyles.page}>
                <header style={errorStyles.header}>
                    <img
                        src="/wiseclean-logo.png"
                        alt="WiseClean"
                        style={errorStyles.logo}
                    />
                </header>
                <main style={errorStyles.main}>
                    <div style={errorStyles.card}>
                        <h1 style={errorStyles.title}>{t('criticalErrorTitle')}</h1>
                        <p style={errorStyles.body}>{t('criticalErrorBody')}</p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={errorStyles.link}>
                            {t('contactSupport')}: {SUPPORT_EMAIL}
                        </a>
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'collect') {
        return (
            <CustomerDetailsForm
                orderId={orderId}
                onSaved={() => setStep('select')}
                onCritical={() => setStep('error')}
            />
        );
    }


    return (
        <div style={selectStyles.page}>
            <header style={selectStyles.header}>
                <img
                    src="/wiseclean-logo.png"
                    alt="WiseClean"
                    style={selectStyles.logo}
                />
            </header>
            <div style={selectStyles.content}>

            {/* Заголовок */}
            {/*<h1
                style={{
                    textAlign: 'center',
                    marginBottom: '40px',
                    fontSize: '24px',
                    color: 'black',
                }}
            >
                {t('selectPayment')}
            </h1>
*/}
            {/* Платежи через банк */}
            <div style={{ marginBottom: '30px' }}>
                <h2
                    style={{
                        fontSize: '20px',
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        color: 'black',
                    }}
                >
                    {t('payViaBank')}{' '}
                    <span
                        style={{
                            color: 'black',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            backgroundColor: 'rgba(108, 200, 217, 0.2)',
                            padding: '3px 6px',
                            borderRadius: '5px',
                        }}
                    >
                        Smart Choice
                    </span>
                </h2>
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        justifyContent: 'center',
                    }}
                >
                    {bankLogos.map((bank) => (
                        <div
                            key={bank.name}
                            onClick={() => handlePayment('bank', bank.code)}
                            style={{
                                border: '1px solid black',
                                borderRadius: '8px',
                                padding: '5px',
                                cursor: 'pointer',
                                width: '70px',
                                height: '70px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'white',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.boxShadow =
                                    '0 6px 12px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow =
                                    '0 4px 8px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            <img
                                src={bank.logo}
                                alt={bank.name}
                                style={{ maxWidth: '100%', maxHeight: '100%' }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Платежи картой */}
            <div style={{ marginBottom: '30px' }}>
                <h2
                    style={{
                        fontSize: '20px',
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        color: 'black',
                    }}
                >
                    {t('payViaCard')}
                </h2>
                <div
                    onClick={() => handlePayment('card')}
                    style={{
                        border: '1px solid black',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        width: '100%',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                    }}
                >
                    <img
                        src={cardPayment.logo}
                        alt="Card Payment"
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                </div>
            </div>
            </div>
        </div>
    );
}

const checkingStyles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    spinner: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: '3px solid var(--wc-brand-light)',
        borderTopColor: 'var(--wc-brand)',
        animation: 'wc-payment-spin 800ms linear infinite',
        marginBottom: '16px',
    },
    text: {
        color: 'var(--wc-text-secondary)',
        fontSize: '15px',
    },
};

const selectStyles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    content: {
        padding: '0 20px 40px',
        maxWidth: '500px',
        margin: '0 auto',
        color: 'var(--wc-text-primary)',
    },
};

const errorStyles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '8px 20px 40px',
    },
    card: {
        width: '100%',
        maxWidth: '460px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '28px 24px',
        textAlign: 'center',
    },
    title: {
        fontSize: '22px',
        fontWeight: 700,
        margin: '0 0 10px 0',
        color: 'var(--wc-text-primary)',
    },
    body: {
        fontSize: '15px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.6,
        margin: '0 0 20px 0',
    },
    link: {
        display: 'inline-block',
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
        textDecoration: 'none',
        wordBreak: 'break-word',
    },
};

Payment.propTypes = {
    // Legacy-form id from PaymentRoute's EveryPay fallback; '' = use the route param.
    orderIdOverride: PropTypes.string,
};

export default Payment;
